import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./AdminQBankReports.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { QBankCode } from "../utils/qbankProfile";
import QBankReferenceEditor from "../components/QBankReferenceEditor";
import {
    getReferenceExcerpt,
    getReferencePage,
    getReferenceSource,
    normalizeReferencesForEditor,
    QBankReference,
    QBankReferenceDraft,
    serializeReferencesForSave,
} from "../utils/qbankReferences";

interface QuestionOption {
    label: string;
    text: string;
}

interface QuestionSnapshot {
    id?: string;
    collection_id?: string;
    collection_title?: string;
    question_text?: string;
    options?: QuestionOption[];
    correct_answer?: string;
    explanation?: string;
    question_references?: QBankReference[];
    specialty?: string;
    difficulty?: string;
    is_published?: boolean;
    explainer_status?: string;
    version_number?: number;
    version_status?: string;
}

interface ReportQuestion extends QuestionSnapshot {
    id: string;
    collection_id: string;
    qbank_collections?: {
        title?: string;
    };
}

interface ReportMessage {
    id: string;
    sent_by?: string;
    sent_to_email: string;
    subject: string;
    body: string;
    resend_message_id?: string;
    created_at: string;
}

interface Report {
    id: string;
    user_id: string;
    question_id: string;
    reason: string;
    details?: string;
    status: string;
    priority?: string;
    resolution_reason?: string;
    admin_notes?: string;
    reporter_email?: string;
    reporter_name?: string;
    question_snapshot?: QuestionSnapshot;
    status_changed_by?: string;
    status_changed_at?: string;
    last_admin_reply_at?: string;
    last_admin_reply_by?: string;
    created_at: string;
    updated_at?: string;
    qbank_questions?: ReportQuestion;
    messages?: ReportMessage[];
}

interface AdminQBankReportsProps {
    selectedReportId?: string;
    bank: QBankCode;
    specialties: string[];
}

const REASON_LABELS: Record<string, string> = {
    incorrect_answer: "Incorrect Answer",
    unclear_question: "Unclear Question",
    typo: "Typo",
    duplicate: "Duplicate",
    outdated: "Outdated Info",
    other: "Other",
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    pending: { label: "Pending", color: "#f59e0b" },
    reviewing: { label: "Reviewing", color: "#00a7bd" },
    resolved: { label: "Resolved", color: "#22c55e" },
    dismissed: { label: "Dismissed", color: "#6b7280" },
};

const PRIORITY_LABELS: Record<string, string> = {
    low: "Low",
    normal: "Normal",
    high: "High",
};

const RESOLUTION_LABELS: Record<string, string> = {
    fixed: "Fixed",
    already_correct: "Already correct",
    duplicate: "Duplicate",
    not_reproducible: "Not reproducible",
    out_of_scope: "Out of scope",
    other: "Other",
};

const STATUS_FILTERS = [
    { value: "open", label: "Open" },
    { value: "pending", label: "Pending" },
    { value: "reviewing", label: "Reviewing" },
    { value: "resolved", label: "Resolved" },
    { value: "dismissed", label: "Dismissed" },
    { value: "all", label: "All" },
];

const readApiError = async (res: Response, fallback: string) => {
    try {
        const data = await res.json();
        return data.error || fallback;
    } catch {
        return fallback;
    }
};

const defaultQuestionForm = () => ({
    question_text: "",
    options: [] as QuestionOption[],
    correct_answer: "",
    specialty: "",
    difficulty: "medium",
    explanation: "",
    question_references: [] as QBankReferenceDraft[],
});

const AdminQBankReports: React.FC<AdminQBankReportsProps> = ({ selectedReportId, bank, specialties }) => {
    const { user } = useAuthContext();
    const [reports, setReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("open");
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [adminNotes, setAdminNotes] = useState("");
    const [statusDraft, setStatusDraft] = useState("pending");
    const [priorityDraft, setPriorityDraft] = useState("normal");
    const [resolutionDraft, setResolutionDraft] = useState("");
    const [questionForm, setQuestionForm] = useState(defaultQuestionForm());
    const [savingQuestion, setSavingQuestion] = useState(false);
    const [replySubject, setReplySubject] = useState("Re: Your QBank question report");
    const [replyBody, setReplyBody] = useState("");
    const [sendingReply, setSendingReply] = useState(false);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    const formatDate = (date?: string) => {
        if (!date) return "Not set";
        return new Date(date).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const currentQuestion = useCallback((report: Report | null) => report?.qbank_questions || null, []);

    const collectionTitle = (report: Report) => (
        report.qbank_questions?.qbank_collections?.title ||
        report.question_snapshot?.collection_title ||
        "Unknown collection"
    );

    const hydrateReportState = useCallback((report: Report) => {
        const question = currentQuestion(report);
        const fallback = report.question_snapshot || {};

        setSelectedReport(report);
        setAdminNotes(report.admin_notes || "");
        setStatusDraft(report.status || "pending");
        setPriorityDraft(report.priority || "normal");
        setResolutionDraft(report.resolution_reason || "");
        setQuestionForm({
            question_text: question?.question_text || fallback.question_text || "",
            options: question?.options || fallback.options || [],
            correct_answer: question?.correct_answer || fallback.correct_answer || "",
            specialty: question?.specialty || fallback.specialty || "",
            difficulty: question?.difficulty || fallback.difficulty || "medium",
            explanation: question?.explanation || fallback.explanation || "",
            question_references: normalizeReferencesForEditor(
                question?.question_references || fallback.question_references
            ),
        });
        setReplySubject("Re: Your QBank question report");
        setReplyBody("");
    }, [currentQuestion]);

    const loadReports = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/reports?user_id=${user.id}&status=${statusFilter}&bank=${bank}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, "Failed to load reports"));
            }
            setReports(await res.json());
        } catch (err) {
            console.error("Failed to load reports:", err);
            (window as any).showNotification?.("Failed to load QBank reports", "error");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, statusFilter, user?.id, bank]);

    const closeWorkflow = useCallback(() => {
        setSelectedReport(null);
    }, []);

    const openReportById = useCallback(async (reportId: string) => {
        if (!user?.id) return;
        setDetailLoading(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/reports/${reportId}?user_id=${user.id}&bank=${bank}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, "Failed to load report"));
            }
            hydrateReportState(await res.json());
        } catch (err) {
            console.error("Failed to load report:", err);
            (window as any).showNotification?.("Failed to open report", "error");
        } finally {
            setDetailLoading(false);
        }
    }, [apiUrl, hydrateReportState, user?.id]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    useEffect(() => {
        if (selectedReportId && user?.id) {
            openReportById(selectedReportId);
        }
    }, [openReportById, selectedReportId, user?.id]);

    useEffect(() => {
        if (!selectedReport) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeWorkflow();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [closeWorkflow, selectedReport]);

    const updateReport = async (reportId: string, updates: Partial<Report>) => {
        if (!user?.id) return;
        setUpdatingId(reportId);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/reports/${reportId}?bank=${bank}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    ...updates,
                }),
            });
            if (!res.ok) {
                throw new Error(await readApiError(res, "Failed to update report"));
            }
            await loadReports();
            if (selectedReport?.id === reportId) {
                await openReportById(reportId);
            }
        } catch (err) {
            console.error("Failed to update report:", err);
            (window as any).showNotification?.("Failed to update report", "error");
        } finally {
            setUpdatingId(null);
        }
    };

    const saveWorkflow = async () => {
        if (!selectedReport) return;
        await updateReport(selectedReport.id, {
            status: statusDraft,
            priority: priorityDraft,
            resolution_reason: resolutionDraft || "",
            admin_notes: adminNotes,
        });
    };

    const persistQuestion = async () => {
        const question = currentQuestion(selectedReport);
        if (!user?.id || !selectedReport || !question?.id) {
            throw new Error("Current question is unavailable for editing");
        }

        const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${question.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                user_id: user.id,
                question_text: questionForm.question_text,
                options: questionForm.options,
                correct_answer: questionForm.correct_answer,
                specialty: questionForm.specialty,
                difficulty: questionForm.difficulty,
                explanation: questionForm.explanation,
                question_references: serializeReferencesForSave(questionForm.question_references),
            }),
        });
        if (!res.ok) {
            throw new Error(await readApiError(res, "Failed to save question"));
        }
        return res.json();
    };

    const saveQuestion = async () => {
        if (!selectedReport) return;
        setSavingQuestion(true);
        try {
            const saved = await persistQuestion();
            setSelectedReport({
                ...selectedReport,
                qbank_questions: {
                    ...(selectedReport.qbank_questions as ReportQuestion),
                    ...saved,
                },
            });
            await loadReports();
            (window as any).showNotification?.("Draft version saved for review", "success");
        } catch (err: any) {
            console.error("Failed to save question:", err);
            (window as any).showNotification?.(err.message || "Failed to save question", "error");
        } finally {
            setSavingQuestion(false);
        }
    };

    const sendReply = async () => {
        if (!selectedReport || !user?.id) return;
        setSendingReply(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/reports/${selectedReport.id}/reply?bank=${bank}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    subject: replySubject,
                    body: replyBody,
                }),
            });
            if (!res.ok) {
                throw new Error(await readApiError(res, "Failed to send reply"));
            }
            const data = await res.json();
            hydrateReportState(data.report);
            await loadReports();
            (window as any).showNotification?.("Reply sent to reporter", "success");
        } catch (err: any) {
            console.error("Failed to send reply:", err);
            (window as any).showNotification?.(err.message || "Failed to send reply", "error");
        } finally {
            setSendingReply(false);
        }
    };

    const addOption = () => {
        const labels = "ABCDEFGHIJ";
        const nextLabel = labels[questionForm.options.length] || String(questionForm.options.length + 1);
        setQuestionForm({
            ...questionForm,
            options: [...questionForm.options, { label: nextLabel, text: "" }],
        });
    };

    const removeOption = (idx: number) => {
        const labels = "ABCDEFGHIJ";
        const originalCorrectIdx = questionForm.options.findIndex((o) => o.label === questionForm.correct_answer);
        const nextOptions = questionForm.options
            .filter((_, i) => i !== idx)
            .map((opt, i) => ({ ...opt, label: labels[i] || String(i + 1) }));
        let nextCorrect = "";
        if (originalCorrectIdx !== -1 && originalCorrectIdx !== idx) {
            const newIdx = originalCorrectIdx > idx ? originalCorrectIdx - 1 : originalCorrectIdx;
            nextCorrect = nextOptions[newIdx]?.label || "";
        }
        setQuestionForm({
            ...questionForm,
            options: nextOptions,
            correct_answer: nextCorrect,
        });
    };

    const renderOptions = (options?: QuestionOption[], correct?: string) => {
        if (!options?.length) return <p className={styles.mutedText}>No options available.</p>;
        return (
            <div className={styles.optionsPreview}>
                {options.map((option) => (
                    <div
                        key={option.label}
                        className={`${styles.optionPreview} ${option.label === correct ? styles.correctOption : ""}`}
                    >
                        <strong>{option.label}.</strong> {option.text}
                        {option.label === correct && <span>Correct</span>}
                    </div>
                ))}
            </div>
        );
    };

    const renderReferences = (references?: QBankReference[]) => {
        if (!references?.length) return null;

        return (
            <div className={styles.referencesPreview}>
                {references.map((reference, index) => {
                    const excerpt = getReferenceExcerpt(reference);
                    const source = getReferenceSource(reference);
                    const page = getReferencePage(reference);

                    return (
                        <div key={index} className={styles.referencePreview}>
                            {excerpt && <p>"{excerpt}"</p>}
                            <span>
                                {source}
                                {page && `, p. ${page}`}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading && reports.length === 0 && !selectedReport) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} />
                <p>Loading reports...</p>
            </div>
        );
    }

    const reportEmail = selectedReport?.reporter_email;
    const canReply = Boolean(reportEmail);
    const question = currentQuestion(selectedReport);
    const snapshot = selectedReport?.question_snapshot;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>Question Reports</h1>
                    <p>Review issues, fix questions, and reply to users from one workflow.</p>
                </div>
                <span className={styles.countBadge}>{reports.length} reports</span>
            </div>

            <div className={styles.filters}>
                {STATUS_FILTERS.map((filter) => (
                    <button
                        key={filter.value}
                        className={`${styles.filterButton} ${statusFilter === filter.value ? styles.filterButtonActive : ""}`}
                        onClick={() => setStatusFilter(filter.value)}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {reports.length === 0 ? (
                <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>No reports</span>
                    <h3>No matching reports</h3>
                    <p>Try a different filter or check back later.</p>
                </div>
            ) : (
                <div className={styles.reportsList}>
                    {reports.map((report) => (
                        <div key={report.id} className={styles.reportCard}>
                            <div className={styles.reportHeader}>
                                <div className={styles.badgeRow}>
                                    <span
                                        className={styles.reasonBadge}
                                        style={{
                                            background: report.reason === "incorrect_answer" ? "var(--theme-danger-bg)" : "var(--theme-control-bg)",
                                            color: report.reason === "incorrect_answer" ? "var(--theme-danger-text)" : "var(--theme-text-soft)",
                                        }}
                                    >
                                        {REASON_LABELS[report.reason] || report.reason}
                                    </span>
                                    <span
                                        className={styles.statusBadge}
                                        style={{ borderColor: STATUS_LABELS[report.status]?.color || "#6b7280" }}
                                    >
                                        {STATUS_LABELS[report.status]?.label || report.status}
                                    </span>
                                    <span className={styles.priorityBadge}>
                                        {PRIORITY_LABELS[report.priority || "normal"] || "Normal"}
                                    </span>
                                </div>
                                <span className={styles.reportDate}>{formatDate(report.created_at)}</span>
                            </div>

                            <div className={styles.questionPreview}>
                                <p className={styles.questionText}>
                                    {(report.qbank_questions?.question_text || report.question_snapshot?.question_text || "Question unavailable").substring(0, 220)}
                                    {(report.qbank_questions?.question_text || report.question_snapshot?.question_text || "").length > 220 && "..."}
                                </p>
                                <div className={styles.metaRow}>
                                    <span className={styles.specialtyTag}>{report.qbank_questions?.specialty || report.question_snapshot?.specialty || "Unknown"}</span>
                                    <span>{collectionTitle(report)}</span>
                                    <span>{report.reporter_email || report.user_id}</span>
                                </div>
                            </div>

                            {report.details && (
                                <div className={styles.detailsSection}>
                                    <strong>User notes</strong>
                                    <p>{report.details}</p>
                                </div>
                            )}

                            <div className={styles.reportActions}>
                                <button
                                    className={styles.resolveButton}
                                    onClick={() => updateReport(report.id, { status: "resolved" })}
                                    disabled={updatingId === report.id}
                                >
                                    Resolve
                                </button>
                                <button
                                    className={styles.dismissButton}
                                    onClick={() => updateReport(report.id, { status: "dismissed" })}
                                    disabled={updatingId === report.id}
                                >
                                    Dismiss
                                </button>
                                <button
                                    className={styles.viewButton}
                                    onClick={() => openReportById(report.id)}
                                    disabled={detailLoading}
                                >
                                    Open Workflow
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {selectedReport && createPortal(
                <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="report-workflow-title">
                    <div className={styles.modalContentWide}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2 id="report-workflow-title">Report Workflow</h2>
                                <p>{REASON_LABELS[selectedReport.reason] || selectedReport.reason} · {formatDate(selectedReport.created_at)}</p>
                            </div>
                            <button className={styles.iconButton} onClick={closeWorkflow} title="Close">
                                x
                            </button>
                        </div>

                        <div className={styles.workflowGrid}>
                            <section className={styles.panel}>
                                <h3>Report</h3>
                                <div className={styles.infoGrid}>
                                    <div>
                                        <label>Reporter</label>
                                        <p>{selectedReport.reporter_name || "Unknown name"}</p>
                                    </div>
                                    <div>
                                        <label>Email</label>
                                        <p>{selectedReport.reporter_email || "Unavailable"}</p>
                                    </div>
                                    <div>
                                        <label>User ID</label>
                                        <p className={styles.monospace}>{selectedReport.user_id}</p>
                                    </div>
                                    <div>
                                        <label>Question ID</label>
                                        <p className={styles.monospace}>{selectedReport.question_id}</p>
                                    </div>
                                </div>

                                <div className={styles.modalSection}>
                                    <label>User Notes</label>
                                    <p className={styles.textBox}>{selectedReport.details || "No additional details provided."}</p>
                                </div>

                                <div className={styles.formRowInline}>
                                    <div className={styles.formRow}>
                                        <label>Status</label>
                                        <select value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}>
                                            {Object.entries(STATUS_LABELS).map(([value, meta]) => (
                                                <option key={value} value={value}>{meta.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.formRow}>
                                        <label>Priority</label>
                                        <select value={priorityDraft} onChange={(e) => setPriorityDraft(e.target.value)}>
                                            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.formRow}>
                                        <label>Resolution</label>
                                        <select value={resolutionDraft} onChange={(e) => setResolutionDraft(e.target.value)}>
                                            <option value="">None</option>
                                            {Object.entries(RESOLUTION_LABELS).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.modalSection}>
                                    <label>Internal Notes</label>
                                    <textarea
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="Notes for admins only..."
                                        rows={4}
                                    />
                                </div>

                                <button
                                    className={styles.resolveButton}
                                    onClick={saveWorkflow}
                                    disabled={updatingId === selectedReport.id}
                                >
                                    Save Workflow
                                </button>
                            </section>

                            <section className={styles.panel}>
                                <h3>Current Question</h3>
                                {question && (
                                    <div className={styles.snapshotMeta}>
                                        <span>v{question.version_number ?? 0}</span>
                                        <span>{(question.version_status || "legacy").replace("_", " ")}</span>
                                        <span>Saving creates or updates a review draft</span>
                                    </div>
                                )}
                                {!question && (
                                    <p className={styles.warningBox}>The current question could not be loaded. You can still review the report snapshot.</p>
                                )}

                                <div className={styles.formRow}>
                                    <label>Question Text</label>
                                    <textarea
                                        value={questionForm.question_text}
                                        onChange={(e) => setQuestionForm({ ...questionForm, question_text: e.target.value })}
                                        rows={4}
                                        disabled={!question}
                                    />
                                </div>

                                <div className={styles.formRow}>
                                    <label>Options</label>
                                    {questionForm.options.map((option, idx) => (
                                        <div key={`${option.label}-${idx}`} className={styles.optionEditRow}>
                                            <input
                                                type="radio"
                                                name="report_correct_answer"
                                                checked={questionForm.correct_answer === option.label}
                                                onChange={() => setQuestionForm({ ...questionForm, correct_answer: option.label })}
                                                disabled={!question}
                                            />
                                            <span>{option.label}</span>
                                            <input
                                                type="text"
                                                value={option.text}
                                                onChange={(e) => {
                                                    const nextOptions = [...questionForm.options];
                                                    nextOptions[idx] = { ...option, text: e.target.value };
                                                    setQuestionForm({ ...questionForm, options: nextOptions });
                                                }}
                                                disabled={!question}
                                            />
                                            {questionForm.options.length > 2 && (
                                                <button type="button" onClick={() => removeOption(idx)} disabled={!question}>x</button>
                                            )}
                                        </div>
                                    ))}
                                    {questionForm.options.length < 6 && (
                                        <button type="button" className={styles.smallButton} onClick={addOption} disabled={!question}>
                                            Add Option
                                        </button>
                                    )}
                                </div>

                                <div className={styles.formRowInline}>
                                    <div className={styles.formRow}>
                                        <label>Specialty</label>
                                        <select
                                            value={questionForm.specialty}
                                            onChange={(e) => setQuestionForm({ ...questionForm, specialty: e.target.value })}
                                            disabled={!question}
                                        >
                                            <option value="">Select specialty</option>
                                            {specialties.map((specialty) => (
                                                <option key={specialty} value={specialty}>{specialty}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.formRow}>
                                        <label>Difficulty</label>
                                        <select
                                            value={questionForm.difficulty}
                                            onChange={(e) => setQuestionForm({ ...questionForm, difficulty: e.target.value })}
                                            disabled={!question}
                                        >
                                            <option value="easy">Easy</option>
                                            <option value="medium">Medium</option>
                                            <option value="hard">Hard</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <label>Explanation</label>
                                    <textarea
                                        value={questionForm.explanation}
                                        onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                                        rows={6}
                                        disabled={!question}
                                    />
                                </div>

                                <div className={styles.formRow}>
                                    <label>References</label>
                                    <QBankReferenceEditor
                                        references={questionForm.question_references}
                                        onChange={(question_references) => setQuestionForm({
                                            ...questionForm,
                                            question_references,
                                        })}
                                        disabled={!question}
                                    />
                                </div>

                                <div className={styles.modalActions}>
                                    <button className={styles.secondaryButton} onClick={saveQuestion} disabled={!question || savingQuestion}>
                                        {savingQuestion ? "Saving..." : "Save as Draft Version"}
                                    </button>
                                </div>
                            </section>

                            <section className={styles.panel}>
                                <h3>Original Snapshot</h3>
                                {snapshot ? (
                                    <>
                                        <div className={styles.snapshotMeta}>
                                            <span>{snapshot.collection_title || "Unknown collection"}</span>
                                            <span>{snapshot.specialty || "Unknown specialty"}</span>
                                            <span>{snapshot.difficulty || "Unknown difficulty"}</span>
                                            <span>{snapshot.is_published ? "Published" : "Unpublished"}</span>
                                        </div>
                                        <p className={styles.textBox}>{snapshot.question_text || "No question text in snapshot."}</p>
                                        {renderOptions(snapshot.options, snapshot.correct_answer)}
                                        {snapshot.explanation && (
                                            <div className={styles.modalSection}>
                                                <label>Snapshot Explanation</label>
                                                <p className={styles.textBox}>{snapshot.explanation}</p>
                                            </div>
                                        )}
                                        {snapshot.question_references && snapshot.question_references.length > 0 && (
                                            <div className={styles.modalSection}>
                                                <label>Snapshot References</label>
                                                {renderReferences(snapshot.question_references)}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className={styles.warningBox}>This legacy report has no saved question snapshot.</p>
                                )}
                            </section>

                            <section className={styles.panel}>
                                <h3>User Reply</h3>
                                {!canReply && (
                                    <p className={styles.warningBox}>
                                        Reporter email is unavailable. User ID: {selectedReport.user_id}
                                    </p>
                                )}
                                <div className={styles.formRow}>
                                    <label>To</label>
                                    <input value={reportEmail || ""} disabled />
                                </div>
                                <div className={styles.formRow}>
                                    <label>Subject</label>
                                    <input
                                        value={replySubject}
                                        onChange={(e) => setReplySubject(e.target.value)}
                                        disabled={!canReply}
                                    />
                                </div>
                                <div className={styles.formRow}>
                                    <label>Message</label>
                                    <textarea
                                        value={replyBody}
                                        onChange={(e) => setReplyBody(e.target.value)}
                                        rows={7}
                                        placeholder="Write a user-facing update..."
                                        disabled={!canReply}
                                    />
                                </div>
                                <button
                                    className={styles.resolveButton}
                                    onClick={sendReply}
                                    disabled={!canReply || sendingReply || !replySubject.trim() || !replyBody.trim()}
                                >
                                    {sendingReply ? "Sending..." : "Send Reply"}
                                </button>

                                <div className={styles.messageHistory}>
                                    <h4>Message History</h4>
                                    {(selectedReport.messages || []).length === 0 ? (
                                        <p className={styles.mutedText}>No admin replies sent yet.</p>
                                    ) : (
                                        selectedReport.messages?.map((message) => (
                                            <div key={message.id} className={styles.messageCard}>
                                                <div>
                                                    <strong>{message.subject}</strong>
                                                    <span>{formatDate(message.created_at)}</span>
                                                </div>
                                                <p>{message.body}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default AdminQBankReports;
